import React from 'react'
import { Dropdown, Progress, Spin, Button, Tooltip, message } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import FTIcon from 'components/FileTypeIcon'

import i18n from 'i18n'
import classNames from 'classnames'

import { ClientJS } from 'clientjs'
import { formatBytes } from 'utils'
import { errorBase } from 'utils/ui'
import { FILE_ACCESS_LEVEL } from 'types/utils'
import { getCachedFile, setCachedFile } from 'utils/attachmentCache'
import { serveAttachmentForPreview } from 'utils/attachmentPreviewTransfer'

import downIcon from '@/assets/images/chevron-down-gray2-24.svg'
import closeIcon from '@/assets/images/icon-clear-20.svg'
// 상수는 named import — helper 기본 export 로 접근하면 eslint import/no-named-as-default-member
// 가 걸린다. 함수는 기존 관례(helper.xxx)를 유지해 mock 교체 지점을 한 곳으로 둔다.
import helper, { PREVIEW_SIZE_LIMIT } from './helper'

import styles from './style.module.scss'

// 다운로드는 수신이 대부분이고 복호는 수 초다. 두 구간을 각각 0→100 으로 쓸어버리면 바가 두 번
// 채워져 고장으로 보이므로 하나의 단조 증가 바에 매핑한다. 업로드가 보내는 phase 는
// 'uploading'/'completing'(uploadManager.js:342,428) 이라 기본 범위로 떨어진다 — 'upload' 라는
// 값은 존재하지 않으므로 그 이름으로 분기를 만들면 죽은 코드가 된다.
const PHASE_RANGE = { net: [0, 90], decrypt: [90, 100] }

const DOWNLOAD_ERROR_KEYS = {
  ABORTED: 'record.attachment.downloadCancelled',
  DOWNLOAD_STALLED: 'record.attachment.downloadStalled',
}

// `stalling` 도 여기 둔다 — 종료 경로 3곳(:종료 · 대기해제 · 성공)이 전부 이것만 펼치므로
// 새 필드가 공짜로 해제된다. 렌더 조건식으로 가리면 새 종료 경로가 생길 때마다 잔류가 재발한다.
const IDLE_PROGRESS = {
  progressBytes: null,
  progressTotal: null,
  progressPhase: null,
  stalling: false,
}

class Attachment extends React.Component {
  state = {
    percent: -1,
    isEnabled: true,
    thumbLoading: false,
    loading: false,
    // 표시용 — 없으면 기존 크기 텍스트로 폴백한다. "유휴" 와 "바이트 불명" 의 판별자는
    // 이 값이 아니라 percent 다 (percent === -1 이 유휴).
    progressBytes: null,
    progressTotal: null,
    progressPhase: null,
    // 무진행 경고 표시 여부. helper 의 정지 가드가 창의 절반에서 켠다.
    stalling: false,
  }

  antIcon = (<LoadingOutlined size="small" spin />)

  // 진행 중인 다운로드를 **시작한** attachment 객체. 취소 수단(xhr·abortDecrypt)은 helper 가
  // 그 객체에 심고(helper.js:311) `cancelDownload` 도 그 객체를 클로저로 붙잡으므로(:584),
  // props 의 객체가 교체되면(언어 변경 등) 새 객체로는 취소가 조용히 no-op 이 된다.
  activeDownloadObj = null

  async componentDidMount() {
    const { attachmentObj } = this.props
    const { file, startEncoding } = attachmentObj
    const { name, type } = file

    if (name === null && type === 'message/rfc822') {
      this.setState({
        thumbLoading: true,
      })
      console.log(' --- file: ', file)
      this.preDownload()
    }

    this.bindProgressHooks(attachmentObj)

    if (startEncoding != null) {
      await startEncoding(this)
    }
  }

  componentDidUpdate({ attachmentObj: prevAttachmentObj }) {
    const { attachmentObj } = this.props
    if (prevAttachmentObj === attachmentObj) return

    // ⚠️ **이전 객체의 훅은 지우지 않는다.** 진행 중인 다운로드·업로드는 시작 시점의 attachment 를
    // 클로저로 붙잡고 있어(helper 의 notifyNetProgress 등), 여기서 해제하면 그 작업의 진행률이
    // 죽어 지금 고치려는 증상이 그대로 재발한다. 해제는 언마운트에서만 한다.
    this.bindProgressHooks(attachmentObj)
  }

  componentWillUnmount() {
    // 언마운트 후 setState 경고 방지 + 진행 중 업로드가 컴포넌트를 붙잡지 않게 훅 해제.
    //
    // ⚠️ **여기서 다운로드를 abort 하지 않는다.** 언마운트는 닫기만이 아니라 상세→편집 전환
    // (RecordWrapper 가 Comp 를 스왑)에서도 일어나므로, abort 를 붙이면 편집 버튼 클릭이 진행 중
    // 다운로드를 파괴한다. 취소는 사용자의 명시적 클릭에서만 한다(설계 D1).
    const { attachmentObj } = this.props
    this.unmounted = true
    if (attachmentObj != null) {
      attachmentObj.onUploadProgress = null
      attachmentObj.onDecodeProgress = null
      attachmentObj.onDownloadProgress = null
      attachmentObj.onDownloadStalling = null
      attachmentObj.onUploadQueued = null
      attachmentObj.onUploadSettled = null
    }
  }

  /**
   * 진행률 훅을 attachment 객체에 심는다.
   *
   * 업로드는 저장 시점에 RecordEdit 이 트리거하므로 이 컴포넌트 인스턴스를 인자로 받을 기회가
   * 없다 — attachment 객체에 콜백을 심어두고 helper 가 그것을 읽어 uploadChunked/decodeToFile 에
   * 넘긴다 (setLoading 의 reactObj 전달과 같은 계열이지만, 호출 시점이 컴포넌트 밖이라 훅 등록
   * 방식을 쓴다).
   *
   * **마운트뿐 아니라 객체가 교체될 때도 다시 심어야 한다.** 언어 변경처럼 파일 목록이 새 객체로
   * 다시 만들어져도 React key 는 파일 id 라 그대로여서(records/helper.js:853) 리마운트가 일어나지
   * 않는다 → componentDidMount 가 다시 돌지 않아 교체된 객체에는 훅이 없고, helper 가 진행률을
   * 조용히 버린다(helper.js:302 `onDownloadProgress == null` 조기 반환). 증상은 "행 스피너만 돌고
   * 진행바·취소 X 가 안 뜨는데 다운로드는 완료" 다 — setLoading 은 객체가 아니라 인스턴스
   * (reactObj)를 통해 가므로 혼자 살아남기 때문이다 (QA 2026-08-14 실측).
   */
  bindProgressHooks = (attachmentObj) => {
    if (attachmentObj == null) return

    attachmentObj.onUploadProgress = this.onChunkProgress
    // onDecodeProgress 는 helper.js decodeChunked 가 이미 읽고 있었으나 세팅하는 곳이 없어
    // 사문이었다 — 여기서 등록하면 신포맷 스트리밍 복호 진행률이 살아난다.
    attachmentObj.onDecodeProgress = this.onChunkProgress
    // 다운로드 수신 구간. helper 가 XHR progress 를 같은 페이로드로 변환해 넘긴다.
    attachmentObj.onDownloadProgress = this.onChunkProgress
    // 무진행 중간 상태. 진행률과 달리 boolean 신호라 percent 계산에 섞지 않는다
    // (onUploadQueued 와 같은 형태의 훅).
    attachmentObj.onDownloadStalling = this.onDownloadStalling
    // 순차 업로드의 대기 표시 (services/attachmentV2/uploadQueue 가 호출)
    attachmentObj.onUploadQueued = this.onUploadQueued
    attachmentObj.onUploadSettled = this.resetProgressIfIncomplete
  }

  /**
   * 업로드가 끝났음을 helper 가 알릴 때 진행률 오버레이를 정리한다.
   *
   * 실패·취소는 진행률 콜백을 더 호출하지 않으므로 percent 가 마지막 값에 갇히고, 첨부 행이
   * 그 비율만큼 회색으로 덮인 채(.progress 는 height:100% 오버레이) opacity 0.5 로 남는다.
   * **성공(100)은 건드리지 않는다** — 기존 500ms 리셋 경로가 완료 표시를 잠깐 보여준 뒤 지운다.
   */
  resetProgressIfIncomplete = () => {
    if (this.unmounted === true) return
    const { percent } = this.state
    if (percent >= 0 && percent < 100) {
      this.setState({ percent: -1, ...IDLE_PROGRESS })
    }
  }

  /** 무진행 경고. 바이트 표기를 잠시 대체하고, 진행이 재개되면 helper 가 false 로 되돌린다. */
  onDownloadStalling = (stalling) => {
    if (this.unmounted === true) return
    this.setState({ stalling: stalling === true })
  }

  /** 순차 업로드에서 자기 차례를 기다리는 동안의 표시. 바는 띄우지 않는다(진행이 아니므로). */
  onUploadQueued = (queued) => {
    if (this.unmounted === true) return
    this.setState(
      queued === true ? { progressPhase: 'queued' } : { ...IDLE_PROGRESS, progressPhase: null },
    )
  }

  /**
   * 청크 매니저의 진행률 페이로드를 기존 percent 상태 머신으로 변환한다.
   * uploadManager·downloadManager 양쪽이 `{bytes, totalBytes, ratio}` 를 주므로 어댑터 하나로 족하다.
   * 100 도달 후 -1 리셋(오버레이 숨김)까지 기존 onProgress 가 처리한다.
   */
  onChunkProgress = ({ ratio, bytes, totalBytes, phase } = {}) => {
    if (this.unmounted === true) return
    const pct = typeof ratio === 'number' && Number.isFinite(ratio) ? ratio : 0
    const clamped = Math.min(Math.max(pct, 0), 1)
    const [lo, hi] = PHASE_RANGE[phase] || [0, 100]
    this.setState({
      progressBytes: typeof bytes === 'number' ? bytes : null,
      progressTotal: typeof totalBytes === 'number' ? totalBytes : null,
      progressPhase: phase != null ? phase : null,
    })
    this.onProgress(null, Math.round(lo + clamped * (hi - lo)))
  }

  /** 진행 중인 청크 업로드 취소. uploadManager 가 signal 을 받아 abortMultipart 까지 호출한다. */
  cancelUpload = (e) => {
    const { attachmentObj } = this.props
    if (e != null) {
      e.stopPropagation()
    }
    attachmentObj?.abortChunkedUpload?.()
  }

  /**
   * 진행 중인 다운로드 취소.
   *
   * `stopPropagation` 이 필수다 — 상세 표면은 행 전체가 `<Dropdown>` trigger 라(render 하단)
   * 막지 않으면 취소와 동시에 메뉴가 열린다. 취소 수단이 xhr/복호 2개라는 사실은 helper 의
   * `cancelDownload` 안에 있고 컴포넌트는 그것만 부른다.
   */
  cancelDownload = (e) => {
    const { attachmentObj } = this.props
    if (e != null && typeof e.stopPropagation === 'function') {
      e.stopPropagation()
    }
    // 진행 중이면 그 다운로드를 시작한 객체를, 아니면 현재 객체를 쓴다.
    ;(this.activeDownloadObj ?? attachmentObj)?.cancelDownload?.()
  }

  /** 진행 중일 때 X 버튼이 무엇을 취소할지 — 업로드가 있으면 업로드, 없으면 다운로드. */
  cancelInProgress = (e) => {
    const { attachmentObj } = this.props
    if (typeof attachmentObj?.abortChunkedUpload === 'function') {
      this.cancelUpload(e)
      return
    }
    this.cancelDownload(e)
  }

  onProgress = (evt, percent) => {
    // 청크 경로를 붙이면서 이 함수가 파트마다 호출된다(500MB 업로드면 60여 회) — 진행률 tick
    // 로그는 콘솔을 덮어 다른 진단을 가리므로 제거했다 (PR #1793 리뷰).
    this.setState({
      percent,
    })

    if (percent >= 100) {
      setTimeout(() => {
        this.setState({
          percent: -1,
          isEnabled: true,
          ...IDLE_PROGRESS,
        })
      }, 500)
    }
  }

  setLoading = (loading) => {
    this.setState({
      loading,
    })
  }

  setFileElem = (ref) => {
    if (ref != null) {
      this.ref = ref
      // console.log(' ---- ref ---- ', ref, ref.innerText)
      const elems = ref.querySelectorAll('span')
      if (elems.length > 0) {
        requestAnimationFrame(() => {
          // const w = elems[1].offsetWidth // + 1
          let w = elems[1].offsetWidth // + 1;
          const wStr = window.getComputedStyle(elems[1], null).width
          if (wStr.substring(wStr.length - 2) === 'px') {
            const num = wStr.substring(0, wStr.length - 2)
            w = window.parseFloat(num)
          }
          if (ref.offsetWidth > w) {
            elems[1].style.maxWidth = `${w}px`
            elems[0].style.maxWidth = `calc(100% - ${w}px)`
          }
        })
      }
    }
  }

  deleteAttachment = (e) => {
    const { enableDelete, attachmentObj } = this.props
    // console.log(' delete attachment')

    if (enableDelete !== false) {
      attachmentObj.delete()
    }

    if (e != null) {
      e.stopPropagation()
    }
  }

  download = (onDownload, options) => {
    const { attachmentObj } = this.props

    // 취소가 겨눌 대상을 여기서 고정한다 (activeDownloadObj 선언부 참조)
    this.activeDownloadObj = attachmentObj

    // 체인을 반환한다 — 호출부가 완료(성공/실패 안내 포함)를 기다릴 수 있어야 한다
    return attachmentObj
      .download(this.onProgress, this, options)
      .then((ret) => {
        console.log('download - ', ret)
        if (onDownload != null) {
          onDownload(ret)
        }
      })
      .catch((e) => {
        console.error('download - ', e)
        // 실패 사유를 콜백에 넘긴다 — 예전에는 errorBase 가 주석 처리된 채 null 만 넘겨서,
        // 다운로드가 어떤 이유로 실패하든 사용자가 받는 피드백이 0건이었다.
        if (onDownload != null) {
          onDownload(null, e)
        }
      })
      .finally(() => {
        this.activeDownloadObj = null
        this.setLoading(false)
        // 복호(신포맷 스트리밍)가 중간에 실패하면 진행률 콜백이 더 오지 않아 percent 가 그 값에
        // 갇힌다 — 업로드와 정확히 같은 실패 모드다(PR #1793 리뷰). onDecodeProgress 를 살린
        // 이상 읽기 경로도 대칭으로 해제해야 한다. 성공(100)은 기존 500ms 경로가 처리한다.
        this.resetProgressIfIncomplete()
      })
  }

  preDownload = () => {
    // `.eml` 썸네일용 자동 다운로드 — 사용자가 시작한 액션이 아니므로 진행률을 띄우지 않는다
    // (마운트마다 바가 번쩍이는 것을 막는다). 실패도 기존처럼 조용히 둔다.
    this.download(
      (ret) => {
        if (ret == null) {
          // 실패 시 `ret.newFileName` 접근이 TypeError 를 던져(콜백은 download 의 .catch 안에서
          // 실행된다) unhandled rejection 이 됐고, 아래 setState 에도 도달하지 못해 **썸네일
          // 스피너가 영구 회전**했다. `.eml` 은 componentDidMount 에서 thumbLoading=true 로
          // 시작하므로 실패 경로에서도 반드시 내려야 한다. (PR #1819 리뷰 minor + 그 부수 문제)
          if (this.unmounted !== true) this.setState({ thumbLoading: false })
          return
        }
        this.downloadObj = ret

        const { attachmentObj } = this.props
        const { file } = attachmentObj

        const newFile = {
          ...file,
        }

        if (ret.newFileName != null) {
          newFile.name = ret.newFileName
        }

        this.setState({
          file: newFile,
          thumbLoading: false,
        })
      },
      { reportProgress: false },
    )
  }

  downloadBackground = (options) => {
    const { attachmentObj } = this.props
    // 캐시 채움은 사용자가 시작한 작업이 아니므로 행의 진행률 바를 움직이면 안 된다.
    // (helper 는 이 플래그로 UI 보고만 막고 수신·정지감지는 그대로 한다)
    attachmentObj
      .download(() => {}, { setLoading: () => {} }, { ...options, reportProgress: false })
      .catch(() => {})
  }

  /**
   * 실패·취소를 사용자에게 알린다. 사용자가 **직접 시작한** 액션에서만 부른다 —
   * 배경 다운로드(캐시 채움)·`.eml` 프리다운로드는 조용한 실패가 옳다.
   *
   * antd 정적 `message` 를 쓰는 이유: `messageApi` 는 첨부 행에 내려오지 않고(attachmentAttrs)
   * 넣으려면 1000줄 헬퍼의 두 호출부를 손대야 한다. 레포에 정적 사용 선례가 4곳 있다.
   * `?.` 가드: 기존 테스트들이 antd 를 mock 하며 `message` 를 제공하지 않는데, 그 파일들은
   * lint 부채가 있어 수정 대상이 아니다(실 antd 가 노출한다는 계약은 별도 테스트로 고정).
   */
  notifyDownloadFailure = (error) => {
    // ⚠️ **언마운트 가드를 두지 않는다.** 다운로드는 언마운트에도 계속 진행되므로(D1) 사용자가
    // 리소스를 닫은 뒤 실패하는 경우가 실제로 있고, 그때 침묵하면 이 PR 이 없애려던 "조용한
    // 실패"가 하필 사용자가 보고한 시나리오에서만 남는다. antd 정적 message 는 body 에 붙는
    // 전역 컨테이너라 React 트리와 무관하며, 레포에도 UI 호스트가 닫히는 async 콜백에서 이를
    // 띄우는 선례가 있다(InquiryModal/index.js:85, UpgradeRequestModal/index.js:66).
    // CLAUDE.md 의 고아 팝업 관례는 사용자가 닫아야 하는 confirmBase/errorBase 를 겨눈다 —
    // 자동 소멸하는 토스트는 그 대상이 아니다.
    const key = DOWNLOAD_ERROR_KEYS[error?.code] || 'record.attachment.downloadFailed'
    message?.warning?.(i18n.t(key))
  }

  downloadFile = async () => {
    const { attachmentObj } = this.props
    const { file } = attachmentObj

    const triggerDownload = (blobUrl) => {
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = file.name
      link.click()
    }

    // 1. 인메모리 캐시
    if (this.downloadObj?.saveFile != null) {
      this.downloadObj.saveFile()
      this.downloadBackground()
      return
    }
    if (this.downloadObj?.downloadPath != null) {
      triggerDownload(this.downloadObj.downloadPath)
      this.downloadBackground()
      return
    }

    // 2. IndexedDB 캐시
    const cacheKey = attachmentObj.url || `${file.name}_${file.size}`
    const cachedBlob = await getCachedFile(cacheKey)
    if (cachedBlob != null) {
      const blobUrl = URL.createObjectURL(cachedBlob)
      this.downloadObj = { downloadPath: blobUrl }
      triggerDownload(blobUrl)
      this.downloadBackground()
      return
    }

    // 3. 서버에서 다운로드 후 IndexedDB에 저장
    await this.download((ret, error) => {
      if (ret == null) {
        this.notifyDownloadFailure(error)
        return
      }
      if (ret.saveFile != null) {
        ret.saveFile()
        fetch(ret.downloadPath)
          .then((r) => r.blob())
          .then((blob) => setCachedFile(cacheKey, blob))
          .catch(() => {})
      }
    })
  }

  showPreview = () => {
    const { attachmentObj } = this.props
    const { file } = attachmentObj
    const { type } = file
    if (type != null) {
      const mimetype = type.toLowerCase()

      if (mimetype != null) {
        if (mimetype.indexOf('image') === 0) {
          attachmentObj.showImage()
        } else if (mimetype.indexOf('video') === 0 || mimetype.indexOf('audio') === 0) {
          attachmentObj.showVideo()
        } else if (mimetype.indexOf('text/') === 0) {
          attachmentObj.showText()
        } else if (mimetype === 'message/rfc822') {
          attachmentObj.showEML()
        }
      }
    }
  }

  handleRestrictedClick = () => {
    const { headerAccessLevel } = this.props
    if (headerAccessLevel === FILE_ACCESS_LEVEL.NO_ACCESS) {
      errorBase({
        title: i18n.t('record.attachment.noAccessTitle'),
        content: i18n.t('record.attachment.noAccessContent'),
        width: 480,
        onButtonProps: {
          className: classNames('type7'),
        },
      })
    }
  }

  openInNewTab = async () => {
    const { attachmentObj } = this.props
    const { file } = attachmentObj

    const client = new ClientJS()
    const isSafari = client.isSafari()
    // Safari는 async 이후 window.open이 차단되므로 가장 먼저 실행
    const viewerWindow = isSafari ? window.open('', '_blank') : null

    const typeParam = helper.isTextViewable(file) ? '&type=text' : ''

    // /viewer 는 cross-origin isolation 되어 opener 의 blob: URL 을 못 읽는다.
    // Blob 을 BroadcastChannel 로 전달(transferId)하고, 미지원 시에만 레거시
    // blob: URL 을 넘긴다.
    const openViewer = (blob, blobUrl) => {
      const transferId = serveAttachmentForPreview(blob, {
        name: file.name,
        mimeType: file.type,
      })
      const viewerUrl =
        transferId != null
          ? `/viewer?ch=${encodeURIComponent(transferId)}&name=${encodeURIComponent(file.name)}${typeParam}`
          : `/viewer?url=${encodeURIComponent(blobUrl)}&name=${encodeURIComponent(file.name)}${typeParam}`
      if (viewerWindow) {
        viewerWindow.location.href = viewerUrl
      } else {
        window.open(viewerUrl, '_blank')
      }
      this.setLoading(false)
    }

    // blob: URL 로부터 자기 탭에서 Blob 을 확보 (opener 는 자기 blob 접근 가능)
    const blobFromUrl = async (blobUrl) => {
      try {
        return await fetch(blobUrl).then((r) => r.blob())
      } catch {
        return null
      }
    }

    const cacheKey = attachmentObj.url || `${file.name}_${file.size}`

    // 1. 인메모리 캐시 (새로고침 전)
    if (this.downloadObj?.downloadPath != null) {
      const blobUrl = this.downloadObj.downloadPath
      openViewer(await blobFromUrl(blobUrl), blobUrl)
      this.downloadBackground({ extraParams: 'purpose=preview' })
      return
    }

    // 2. IndexedDB 캐시 (새로고침 후, 1시간 이내)
    const cachedBlob = await getCachedFile(cacheKey)
    if (cachedBlob != null) {
      const blobUrl = URL.createObjectURL(cachedBlob)
      this.downloadObj = { downloadPath: blobUrl }
      openViewer(cachedBlob, blobUrl)
      this.downloadBackground({ extraParams: 'purpose=preview' })
      return
    }

    // 3. 서버에서 다운로드
    this.download(
      async (ret) => {
        if (ret == null) {
          if (viewerWindow) viewerWindow.close()
          errorBase({
            title: 'Error',
            content: 'Failed to load file data',
          })
          return
        }

        this.downloadObj = ret
        const blob = await blobFromUrl(ret.downloadPath)
        openViewer(blob, ret.downloadPath)

        // 백그라운드에서 IndexedDB에 저장 (이미 확보한 blob 재사용)
        if (blob != null) setCachedFile(cacheKey, blob).catch(() => {})
      },
      { extraParams: 'purpose=preview' },
    )
  }

  fetchByXhr = (url) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.responseType = 'blob'
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 400) resolve(xhr.response)
        else reject(new Error(`HTTP ${xhr.status}`))
      })
      xhr.addEventListener('error', () => reject(new Error('Network error')))
      xhr.open('GET', url, true)
      xhr.send()
    })
  }

  runSingleTest = async (method, url, file, decode) => {
    const dlStart = performance.now()
    const blob =
      method === 'xhr'
        ? await this.fetchByXhr(url)
        : await fetch(url).then((r) => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`)
            return r.blob()
          })
    const dlEnd = performance.now()

    const tmpFile = new File([blob], file.name, { type: file.type })
    await decode(tmpFile)
    const decEnd = performance.now()

    return {
      download: dlEnd - dlStart,
      decrypt: decEnd - dlEnd,
      total: decEnd - dlStart,
    }
  }

  // compareDownload = async () => {
  //   const { attachmentObj } = this.props
  //   const { file, resourceData, url: currentUrl } = attachmentObj
  //   const presignUrl = resourceData?.presign_url

  //   const label = `"${file.name}" (${formatBytes(file.size, 0, true)})`
  //   console.log(`\n[Test] === 다운로드 속도 비교: ${label} (3회 반복, 랜덤 순서) ===`)
  //   console.log('[Test] 백엔드 URL:', currentUrl)
  //   console.log('[Test] S3 presign_url:', presignUrl)

  //   if (!presignUrl) {
  //     console.warn('[Test] presign_url 없음 — S3 비교 불가')
  //     return
  //   }

  //   const ROUNDS = 3
  //   const results = { backend: [], s3: [] }

  //   // 6회 실행: backend 3회 + s3 3회를 랜덤 순서로 섞기
  //   const schedule = []
  //   for (let i = 0; i < ROUNDS; i += 1) {
  //     schedule.push({ type: 'backend', idx: i })
  //     schedule.push({ type: 's3', idx: i })
  //   }
  //   for (let i = schedule.length - 1; i > 0; i -= 1) {
  //     const j = Math.floor(Math.random() * (i + 1))
  //     const tmp = schedule[i]
  //     schedule[i] = schedule[j]
  //     schedule[j] = tmp
  //   }

  //   console.log('[Test] 실행 순서:', schedule.map((s) => s.type).join(' → '))

  //   for (let i = 0; i < schedule.length; i += 1) {
  //     const { type, idx } = schedule[i]
  //     try {
  //       if (type === 'backend') {
  //         // eslint-disable-next-line no-await-in-loop
  //         const r = await this.runSingleTest('xhr', currentUrl, file, attachmentObj.decode)
  //         results.backend.push(r)
  //         console.log(
  //           `[Test] 백엔드 #${idx + 1} — DL: ${r.download.toFixed(0)}ms, 복호화: ${r.decrypt.toFixed(0)}ms, 총: ${r.total.toFixed(0)}ms`,
  //         )
  //       } else {
  //         // eslint-disable-next-line no-await-in-loop
  //         const r = await this.runSingleTest('fetch', presignUrl, file, attachmentObj.decode)
  //         results.s3.push(r)
  //         console.log(
  //           `[Test] S3     #${idx + 1} — DL: ${r.download.toFixed(0)}ms, 복호화: ${r.decrypt.toFixed(0)}ms, 총: ${r.total.toFixed(0)}ms`,
  //         )
  //       }
  //     } catch (e) {
  //       console.error(`[Test] ${type} #${idx + 1} 실패:`, e)
  //     }
  //   }

  //   const avg = (arr, key) => arr.reduce((sum, r) => sum + r[key], 0) / arr.length

  //   if (results.backend.length > 0 && results.s3.length > 0) {
  //     const backendAvg = avg(results.backend, 'total')
  //     const s3Avg = avg(results.s3, 'total')
  //     const diff = Math.abs(backendAvg - s3Avg)
  //     const faster = backendAvg < s3Avg ? '백엔드' : 'S3'

  //     console.log(`\n[Test] ── 평균 결과 (${ROUNDS}회) ──`)
  //     console.log(
  //       `[Test] 백엔드 평균 — DL: ${avg(results.backend, 'download').toFixed(0)}ms, 복호화: ${avg(results.backend, 'decrypt').toFixed(0)}ms, 총: ${backendAvg.toFixed(0)}ms`,
  //     )
  //     console.log(
  //       `[Test] S3     평균 — DL: ${avg(results.s3, 'download').toFixed(0)}ms, 복호화: ${avg(results.s3, 'decrypt').toFixed(0)}ms, 총: ${s3Avg.toFixed(0)}ms`,
  //     )
  //     console.log(
  //       `[Test] 결과: ${faster}가 ${diff.toFixed(0)}ms 더 빠름 (${((diff / Math.max(backendAvg, s3Avg)) * 100).toFixed(1)}% 차이)`,
  //     )
  //   }
  //   console.log(`[Test] === 비교 완료 ===\n`)
  // }

  getDropDownAttrs = () => {
    const { file } = this.state
    const { enablePreview, downloadable, headerAccessLevel, attachmentObj } = this.props
    // 크기 게이트의 입력은 **props 의 파일 메타**다. `this.state.file` 은 초기 state 에 없고
    // preDownload(.eml 전용 경로)에서만 채워져 대부분 undefined 이며, 그러면 size 불명으로
    // 떨어져 게이트가 통째로 무력화된다. 아래 view 분기가 원래부터 props 를 쓰는 것도 같은 이유다.
    // createAttachment / createAttachmentByResource 둘 다 `file: {name, size, type}` 을 채운다.
    const attachmentFile = attachmentObj.file

    const items = []

    if (headerAccessLevel === FILE_ACCESS_LEVEL.NO_ACCESS) {
      return null
    }

    const isPreviewAllowed = headerAccessLevel !== FILE_ACCESS_LEVEL.NO_ACCESS
    const isDownloadAllowed = headerAccessLevel !== FILE_ACCESS_LEVEL.PREVIEW_ONLY

    // 미리보기 3단 정책(설계 §5.3): 미디어는 무제한, 그 외는 PREVIEW_SIZE_LIMIT 초과 시
    // 항목을 비활성 + 이유를 툴팁으로 알린다. 항목을 아예 없애면 "왜 미리보기가 없나"를
    // 알 수 없어 설계가 요구한 "다운로드 전용 + 안내"를 충족하지 못한다.
    // `?.` + 기본값 true: 기존 테스트들이 helper 모듈을 4개 함수만 담은 mock 으로 대체해서,
    // 새 함수를 그대로 호출하면 부채 파일(index.test.js 60건)의 mock 을 손대야 한다.
    // 소스 측 mock-호환 가드 + "실 helper 가 이 함수를 노출한다"는 보증 테스트 조합으로 처리한다
    // (planning-rules 근본원인14/18 의 문서화된 패턴). 기본값은 기존 동작(미리보기 허용)이다.
    const previewSizeAllowed = helper.isPreviewSizeAllowed?.(attachmentFile) ?? true
    const previewItemProps = previewSizeAllowed
      ? { label: i18n.t('record.menu.preview') }
      : {
          disabled: true,
          // disabled 요소에 툴팁을 붙일 때 antd 는 이벤트를 받을 wrapper 노드를 요구한다
          label: (
            <Tooltip
              title={i18n.t('record.attachment.previewSizeExceeded', {
                limit: formatBytes(PREVIEW_SIZE_LIMIT, 0, true),
              })}
            >
              <span>{i18n.t('record.menu.preview')}</span>
            </Tooltip>
          ),
        }

    if (enablePreview !== false && helper.isPreviewEnable(file) === true) {
      items.push({
        ...previewItemProps,
        key: 'preview',
      })
    }

    if (helper.isViewEnabled(attachmentFile) && isPreviewAllowed) {
      items.push({
        ...previewItemProps,
        key: 'view',
      })
    }

    if (downloadable !== false && isDownloadAllowed) {
      items.push({
        label: i18n.t('common.title.download'),
        key: 'download',
      })
    }

    if (items.length === 0) {
      return null
    }

    const menu = {
      items,
      onClick: (e) => {
        console.log(' --- onMenuClick : ', e.key)

        if (e.key === 'download') {
          this.downloadFile()
        } else if (e.key === 'delete') {
          this.deleteAttachment()
        } else if (e.key === 'preview') {
          this.showPreview()
        } else if (e.key === 'view') {
          this.openInNewTab()
        }
        // else if (e.key === 'test') {
        //   this.compareDownload()
        // }
      },
      // selectable: true,
      // selectedKeys: [searchType],
    }

    const dropdownAttrs = {
      overlayClassName: classNames('contextMenu', styles.dropdown),
      menu,
      trigger: ['click'],
      arrow: false,
      placement: 'bottomRight',
      // destroyPopupOnHide: false,
      align: {
        offset: [0, 0],
      },
    }

    return dropdownAttrs
  }

  render() {
    const {
      percent,
      isEnabled,
      thumbLoading,
      loading,
      progressBytes,
      progressTotal,
      progressPhase,
      stalling,
    } = this.state
    const { attachmentObj, enableDelete, forEditor } = this.props
    const { file } = attachmentObj

    const { name, size, type } = file

    const isProgressing = percent >= 0 && percent <= 100
    // ⚠️ `size={2}` 는 **두께 2px 가 아니다** — antd 5 의 `getSize` 는 숫자 하나를 받으면
    // `[width, height] = [size, size]` 로 쓰고(progress/utils.js:79-80), Line 은 그 width 를
    // `outerStyle.width` 에 인라인으로 넣는다(progress/Line.js:141-143). 즉 바 전체가 **2px 폭**이
    // 되어 보이지 않는다(QA 2026-08-12 실측 — 바이트 텍스트는 나오는데 바만 안 보였다).
    // 객체로 주면 width 가 undefined → -1 → `width: '100%'` 로 떨어진다.
    const progressElem = isProgressing ? (
      <Progress
        className={styles.progress}
        percent={percent}
        showInfo={false}
        size={{ height: 2 }}
        strokeColor="#3362ff"
      />
    ) : (
      ''
    )

    // 취소는 진행 중일 때만 가능하다. helper 가 업로드 시작 시 abortChunkedUpload 를 세팅하고
    // 끝나면 null 로 비우므로(uploadChunkedTo finally), 이 값의 유무가 곧 "취소 가능" 상태다.
    const canCancelUpload = isProgressing && typeof attachmentObj.abortChunkedUpload === 'function'
    // 다운로드는 `cancelDownload` 가 항상 정의돼 있어(commonProps) 판별자가 될 수 없다 —
    // 어느 구간을 돌고 있는지는 phase 가 알려준다.
    const canCancelDownload =
      isProgressing && (progressPhase === 'net' || progressPhase === 'decrypt')
    const canCancel = canCancelUpload || canCancelDownload

    const thumbImg = (
      <div className={styles.fileIcon}>
        <FTIcon colorType="multiColor" fileName={name} />
      </div>
    )

    const wrapperClsMap = {}
    const clsMap = {}

    clsMap[styles.icon] = true
    wrapperClsMap.iconItem = true
    clsMap[type] = true
    // `isProgressing` 클래스는 opacity 0.5 를 걸던 유일한 용도가 없어져 함께 제거했다.
    // 남겨두면 `styles.isProgressing` 이 undefined 라 클래스 목록에 리터럴 "undefined" 가 붙는다.
    clsMap.forEditor = forEditor === true

    const isClosable = isEnabled && enableDelete !== false

    clsMap[styles.closable] = isClosable
    // 편집 화면 저장 중 폼 Spin 잠금(베일) 위로 이 행만 올려 취소 X 가 눌리게 한다.
    // 조건을 canCancelUpload 보다 넓히면 대기 중/완료 행의 X(=삭제)까지 눌려 저장 중
    // 삭제 사고가 열린다 (upload_cancel_clickthrough.md DD2 — 순차 큐라 취소 가능 행은 항상 1개).
    clsMap[styles.cancelElevated] = canCancelUpload

    const nameTxt = name || ''

    // 확장자가 표시되게 하거나, 감추는 걸 여기서 결정.
    let pos = -1 // nameTxt.lastIndexOf('.')

    if (pos < 0) {
      pos = nameTxt.length
    }

    const front = nameTxt.substring(0, pos)
    const end = nameTxt.substring(pos)

    const filenameElem = (
      <div
        ref={(ref) => {
          this.setFileElem(ref)
        }}
        className={classNames(styles.filename)}
      >
        <span className={styles.front}>{front}</span>
        <span className={styles.end}>{end}</span>
      </div>
    )

    const fileExt = nameTxt.substring(nameTxt.lastIndexOf('.') + 1, nameTxt.length)
    const dropdownAttrs = this.getDropDownAttrs()

    const { headerAccessLevel } = this.props
    const isNoAccess = enableDelete === false && headerAccessLevel === FILE_ACCESS_LEVEL.NO_ACCESS
    const isDropdown = dropdownAttrs != null && enableDelete === false
    const isViewOnlyAndNotDragDocs =
      helper.isNotDragDocsExtension(file) && headerAccessLevel === FILE_ACCESS_LEVEL.PREVIEW_ONLY

    // 진행 중 취소 버튼. 문구는 무엇을 취소하는지에 따라 갈린다.
    const cancelLabel = canCancelUpload
      ? i18n.t('record.attachment.cancelUpload')
      : i18n.t('record.attachment.cancelDownload')
    // 아이콘 alt 를 비우는 이유: 버튼이 aria-label 로 이름을 갖고 있으므로 아이콘은 장식이다
    // (안 비우면 중복 낭독). 기존 alt="menu" 는 옆 ▼ 에서 복사돼 온 값이라 X 에는 맞지 않았다.
    // 취소 X 와 삭제 X 는 픽셀 단위로 같은 회색 아이콘이라, 업로드 완료 찰나에 시각 변화 0 으로
    // 삭제로 되돌아간다(늦은 클릭이 삭제가 됨). 진행 중에만 원형 틴트를 깔아 두 상태를 구분한다 —
    // 아이콘 자체는 fill 이 하드코딩(icon-clear-20.svg #9CA3AF)돼 CSS color 로 물들지 않는다.
    const cancelBtn = (
      <Button
        icon={<img src={closeIcon} alt="" />}
        onClick={this.cancelInProgress}
        className={classNames(styles.closeIcon, styles.cancelActive)}
        title={cancelLabel}
        aria-label={cancelLabel}
      />
    )

    let menuElem = null
    if (isNoAccess || isViewOnlyAndNotDragDocs) {
      menuElem = null
    } else if (isDropdown) {
      // 상세 표면의 기본 컨트롤은 ▼ 다. 다운로드는 이 표면에서 시작되므로, 진행 중에는 ▼ 를
      // 취소 X 로 교체해야 취소 수단이 생긴다. 진행 중 메뉴로 다른 액션(미리보기·삭제)을
      // 시작하는 것 자체가 위험하므로 잠기는 것이 옳다.
      menuElem = canCancel ? (
        cancelBtn
      ) : (
        <div className={styles.menu} style={{ cursor: 'pointer' }}>
          <img src={downIcon} alt="menu" />
        </div>
      )
    } else if (canCancel) {
      // 업로드 진행 중에는 같은 X 버튼이 **삭제 대신 취소**로 동작한다. 100px 폭 첨부 행에
      // 경쟁 컨트롤을 하나 더 넣지 않으려는 선택이고, 진행 중 삭제는 서버에 올라가는 중인
      // 파일을 목록에서만 지워 취소 없이 방치하게 되므로 그 자체로 옳지 않다.
      menuElem = cancelBtn
    } else {
      // 파괴적 동작인데 이름이 없어 아이콘 alt("menu")가 버튼 이름으로 읽히고 있었다 —
      // 스크린리더가 "menu, 버튼"으로 낭독. 같은 자리의 취소 버튼과 대칭을 맞춘다.
      const deleteLabel = i18n.t('record.attachment.deleteFile')
      menuElem = (
        <Button
          icon={<img src={closeIcon} alt="" />}
          onClick={this.deleteAttachment}
          className={styles.closeIcon}
          title={deleteLabel}
          aria-label={deleteLabel}
        />
      )
    }

    // 수 분 걸리는 작업에는 비율만으로 부족하다 — 남은 양이 필요하다. 형식은 기존 내보내기
    // 문구(`topBar.bulkExport.progressText` "처리 중: {{current}} / {{total}}") 와 같은 계열.
    // 복호 구간은 바이트가 다시 0 부터 오르므로 전용 문구를 쓴다.
    let sizeText
    if (stalling) {
      // 끊긴 뒤 30초를 아무 표시 없이 기다리게 하지 않는다 — 대기·복호보다 우선이다
      sizeText = i18n.t('record.attachment.connectionChecking')
    } else if (progressPhase === 'queued') {
      sizeText = i18n.t('record.attachment.uploadWaiting')
    } else if (progressPhase === 'decrypt') {
      sizeText = i18n.t('record.attachment.decrypting')
    } else if (progressBytes != null && progressTotal != null) {
      sizeText = `${formatBytes(progressBytes, 0)} / ${formatBytes(progressTotal, 0)}`
    } else {
      sizeText = formatBytes(size, 0, true)
    }

    const content = (
      <div className={styles.fileInfo} style={{ cursor: 'pointer' }}>
        <div role="button" tabIndex="-1" onKeyDown={() => {}}>
          {filenameElem}
          <div className={styles.line2}>
            <span className={styles.fileExt}>{fileExt}</span>
            <span className={styles.fileSize}>{sizeText}</span>
          </div>
          {/* 상단 Spin tip 은 maxHeight 400px 안에만 떠서(antd spin/style/index.js:81) 필드가 많은
              폼에선 화면 밖이었다 — 안내를 진행 중인 행, 즉 사용자 시선으로 옮긴다.
              업로드 취소 가능일 때만: `대기 중` 행의 X 는 삭제라 캡션이 유도 오류가 되고,
              다운로드 표면(상세)은 폼 잠금이 없어 X 가 이미 선명하다. */}
          {canCancelUpload && (
            <div className={styles.cancelCaption}>
              {i18n.t('record.attachment.uploadCancelHint')}
            </div>
          )}
          {thumbImg}
        </div>
        {menuElem}
      </div>
    )

    // 진행률이 표시되는 동안에는 행 단위 Spin 을 끈다. 두 가지를 동시에 해결한다 —
    // ① `.ant-spin-blur` 가 행 내용(파일명·바이트·취소 X)을 흐리게 만들어 "취소 버튼이 없다"로
    //    읽히게 했다(QA 2026-08-12) ② 바와 바이트가 이미 진행을 알리는데 원형 스피너가 중복이다.
    // `.eml` 프리다운로드는 `reportProgress: false` 라 진행률이 없어 스피너가 그대로 유지된다.
    const showLoading = (loading === true || thumbLoading === true) && !isProgressing
    const isRestricted = isNoAccess || isViewOnlyAndNotDragDocs

    let attachmentItem
    if (isRestricted) {
      attachmentItem = (
        <div
          role="button"
          tabIndex={0}
          className={classNames(styles.attachmentItem, 'attachmentItem', clsMap)}
          onClick={this.handleRestrictedClick}
          onKeyDown={(e) => e.key === 'Enter' && this.handleRestrictedClick()}
        >
          <Spin indicator={this.antIcon} spinning={showLoading}>
            {content}
          </Spin>
          {progressElem}
        </div>
      )
    } else {
      attachmentItem = (
        <div className={classNames(styles.attachmentItem, 'attachmentItem', clsMap)}>
          <Spin indicator={this.antIcon} spinning={showLoading}>
            {content}
          </Spin>
          {progressElem}
        </div>
      )
    }

    return (
      <div className={styles.attachmentBox}>
        {isDropdown ? (
          // 진행 중에는 드롭다운을 잠근다. 트리거가 **행 전체**라(자식 div 를 cloneElement 해
          // `dropdown-trigger` 를 붙인다) 취소 X 를 눌러도 메뉴가 함께 열려 "취소가 없다"로
          // 읽혔다(QA 2026-08-12 — 행 아무 곳이나 같은 동작). `disabled` 면 antd 가
          // `triggerActions = []` 로 비워 어떤 클릭도 열지 않는다(dropdown/dropdown.js:111).
          // 진행 중 메뉴로 다른 액션(미리보기·삭제)을 시작하는 것 자체가 위험하므로 설계 의도와도 맞다.
          <Dropdown {...dropdownAttrs} disabled={canCancel}>
            <div className={classNames(styles.attachmentItem, 'attachmentItem', clsMap)}>
              <Spin indicator={this.antIcon} spinning={showLoading}>
                {content}
              </Spin>
              {progressElem}
            </div>
          </Dropdown>
        ) : (
          attachmentItem
        )}
      </div>
    )
  }
}

export default Attachment
